import { Helmet } from 'react-helmet-async'
import { useParams, Link } from 'react-router-dom'
import { FaCalendar, FaClock, FaArrowLeft, FaLinkedin, FaTwitter, FaFacebook } from 'react-icons/fa'

interface BlogPostData {
  slug: string
  title: string
  category: string
  date: string
  readTime: string
  author: string
  authorRole: string
  excerpt: string
}

const blogPostsData: Record<string, BlogPostData> = {
  'bitcoin-mining-tax-strategy-2025': {
    slug: 'bitcoin-mining-tax-strategy-2025',
    title: 'Bitcoin Mining Tax Strategy: Maximize 2025 Deductions',
    category: 'Tax & Compliance',
    date: '2025-01-15',
    readTime: '8 min read',
    author: 'Tax Advisory Team',
    authorRole: 'Crypto Tax Specialists',
    excerpt: 'Learn how to optimize your bitcoin mining operations for maximum tax efficiency in 2025.'
  },
  'proof-of-reserves-explained': {
    slug: 'proof-of-reserves-explained',
    title: 'Understanding Proof of Reserves: A Comprehensive Guide',
    category: 'Technical Guide',
    date: '2025-01-12',
    readTime: '10 min read',
    author: 'Technical Team',
    authorRole: 'Blockchain Security Experts',
    excerpt: 'A deep dive into proof of reserves methodologies and their importance in the crypto ecosystem.'
  }
}

const BlogPost = () => {
  const { slug } = useParams()
  const post = slug ? blogPostsData[slug] : blogPostsData['proof-of-reserves-explained']
  const currentPost = post || blogPostsData['proof-of-reserves-explained']

  return (
    <>
      <Helmet>
        <title>{currentPost.title} - Proof of Reserves Auditor</title>
        <meta name="description" content={currentPost.excerpt} />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary via-primary-dark to-primary text-white py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 20px 20px, rgba(255,255,255,0.1) 2px, transparent 0)',
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          
          <div className="container-custom max-w-4xl text-center relative z-10">
            <Link to="/blog" className="inline-flex items-center gap-2 text-accent hover:text-white transition-colors mb-6">
              <FaArrowLeft />
              <span>Back to All Articles</span>
            </Link>
            
            <div className="mb-4">
              <span className="inline-block bg-accent px-4 py-2 rounded-full text-sm font-semibold">
                {currentPost.category}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {currentPost.title}
            </h1>
            
            <div className="flex items-center justify-center gap-6 text-gray-200 mb-4">
              <div className="flex items-center gap-2">
                <FaCalendar className="text-accent" />
                <span>{new Date(currentPost.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-2">
                <FaClock className="text-accent" />
                <span>{currentPost.readTime}</span>
              </div>
            </div>
            
            <p className="text-gray-200">
              By {currentPost.author} • {currentPost.authorRole}
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-white">
          <div className="container-custom max-w-4xl">
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                {currentPost.excerpt}
              </p>

              {slug === 'bitcoin-mining-tax-strategy-2025' ? (
                <>
                  <h2 className="text-3xl font-bold text-primary mt-12 mb-6">Understanding Mining Income</h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    Bitcoin mining rewards are considered ordinary income at the fair market value on the date of receipt. This creates immediate tax liability, even if you hold the mined bitcoin. Proper planning is essential to manage this tax burden effectively.
                  </p>

                  <h2 className="text-3xl font-bold text-primary mt-12 mb-6">Key Deduction Strategies for 2025</h2>
                  <div className="space-y-6 mb-8">
                    <div className="bg-gray-50 border-l-4 border-accent p-6">
                      <h3 className="text-xl font-bold text-primary mb-3">1. Equipment Depreciation</h3>
                      <p className="text-gray-700">
                        Mining equipment qualifies for accelerated depreciation under Section 179 or bonus depreciation rules. In 2025, you can potentially deduct up to $1.22 million in equipment purchases immediately, subject to phase-out thresholds.
                      </p>
                    </div>

                    <div className="bg-gray-50 border-l-4 border-accent p-6">
                      <h3 className="text-xl font-bold text-primary mb-3">2. Electricity Costs</h3>
                      <p className="text-gray-700">
                        Electricity is typically your largest operational expense and is fully deductible. Maintain detailed records of power consumption specifically attributable to mining operations to maximize this deduction.
                      </p>
                    </div>

                    <div className="bg-gray-50 border-l-4 border-accent p-6">
                      <h3 className="text-xl font-bold text-primary mb-3">3. Facility and Infrastructure</h3>
                      <p className="text-gray-700">
                        Costs for building improvements, cooling systems, and security infrastructure may qualify for various deduction methods including immediate expensing or depreciation over their useful life.
                      </p>
                    </div>
                  </div>

                  <h2 className="text-3xl font-bold text-primary mt-12 mb-6">Compliance Best Practices</h2>
                  <ul className="list-disc list-inside text-lg text-gray-600 space-y-3 mb-8 ml-4">
                    <li>Maintain separate wallets for mined bitcoin versus purchased bitcoin</li>
                    <li>Document the fair market value at the time of each mining reward</li>
                    <li>Keep detailed records of all operational expenses</li>
                    <li>Consider quarterly estimated tax payments to avoid penalties</li>
                    <li>Consult with crypto-native tax professionals for complex situations</li>
                  </ul>
                </>
              ) : (
                <>
                  <h2 className="text-3xl font-bold text-primary mt-12 mb-6">What is Proof of Reserves?</h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    Proof of Reserves (PoR) is an independent verification process that confirms cryptocurrency platforms have the assets they claim to hold. This transparency mechanism helps protect users by ensuring platforms maintain sufficient reserves to cover customer deposits.
                  </p>

                  <h2 className="text-3xl font-bold text-primary mt-12 mb-6">Why It Matters</h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    In the aftermath of high-profile exchange failures, proof of reserves has become essential for building trust in the digital asset ecosystem. Regular PoR audits demonstrate a platform's solvency and commitment to transparency.
                  </p>

                  <div className="bg-blue-50 border-l-4 border-accent p-6 my-8">
                    <p className="text-lg text-gray-700 italic">
                      "Transparency through regular proof of reserves audits is no longer optional—it's a fundamental requirement for building trust in the cryptocurrency industry."
                    </p>
                  </div>

                  <h2 className="text-3xl font-bold text-primary mt-12 mb-6">Types of Proof of Reserves</h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    There are several approaches to implementing proof of reserves:
                  </p>
                  <ul className="list-disc list-inside text-lg text-gray-600 space-y-3 mb-8 ml-4">
                    <li><strong>Conventional Audits:</strong> Traditional attestation by certified accountants</li>
                    <li><strong>Merkle Tree Verification:</strong> Cryptographic proof with user verification</li>
                    <li><strong>On-Chain Verification:</strong> Smart contract-based public verification</li>
                    <li><strong>Real-Time Monitoring:</strong> Continuous automated verification</li>
                  </ul>

                  <h2 className="text-3xl font-bold text-primary mt-12 mb-6">Best Practices</h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    To maximize the value of proof of reserves audits:
                  </p>
                  <ul className="list-disc list-inside text-lg text-gray-600 space-y-3 mb-8 ml-4">
                    <li>Conduct audits regularly (monthly or more frequently)</li>
                    <li>Use independent, certified auditors</li>
                    <li>Make reports publicly accessible</li>
                    <li>Include both assets and liabilities in verification</li>
                    <li>Consider multiple verification methods for comprehensive coverage</li>
                  </ul>
                </>
              )}

              <h2 className="text-3xl font-bold text-primary mt-12 mb-6">Conclusion</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {slug === 'bitcoin-mining-tax-strategy-2025' 
                  ? 'Proper tax planning can save mining operations significant amounts annually. Work with crypto-native tax professionals to implement these strategies throughout the year, not just at tax time.'
                  : 'Proof of reserves auditing represents a critical step toward greater transparency and accountability in the cryptocurrency industry. As the sector matures, regular PoR verification will likely become a standard expectation for all digital asset service providers.'
                }
              </p>
            </div>

            {/* Share Buttons */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Share this article</h3>
              <div className="flex gap-4">
                <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
                  <FaLinkedin />
                  <span>LinkedIn</span>
                </button>
                <button className="flex items-center gap-2 bg-blue-400 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-colors">
                  <FaTwitter />
                  <span>Twitter</span>
                </button>
                <button className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors">
                  <FaFacebook />
                  <span>Facebook</span>
                </button>
              </div>
            </div>

            {/* CTA Section */}
            <div className="mt-16 p-8 bg-gradient-to-br from-primary to-primary-dark rounded-lg text-white text-center">
              <h3 className="text-2xl font-bold mb-4">Ready to Implement Proof of Reserves?</h3>
              <p className="text-gray-200 mb-6">
                Contact our team to discuss your audit needs and find the right solution for your platform.
              </p>
              <Link to="/contact" className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                Get Started
              </Link>
            </div>

            {/* Back to Blog */}
            <div className="mt-12 text-center">
              <Link to="/blog" className="inline-flex items-center gap-2 text-accent hover:text-primary font-semibold transition-colors">
                <FaArrowLeft />
                <span>Back to All Articles</span>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default BlogPost